import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { dataFormat, formRequiredRules } from 'helper';
import { Lead } from 'pages/leadGen/LeadGenInterface';
import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CommonInput, SelectInput } from 'sharedComponents/inputGroup';
import { AutoCompleteAddress } from 'sharedComponents/inputGroup/AutoCompleteAddress';
import CustomLoader from 'sharedComponents/loader/CustomLoader';
import { postPublicLeadCreationOld } from 'store/CreditScoreState';
import { postTradeInLeadCreation } from 'store/TradeInState';
import { RootState } from 'store/store';
import { PROVINCE } from '../CreditConstants';
import Logger from 'utils/logger';

const Step2: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const zipCode = authenticateClientData.data?.widgetData?.zipCode;

  const associateDealerCode = authenticateClientData?.data?.widgetData?.associateDealerCode;
  const leadSourceId = authenticateClientData?.data?.widgetData?.leadSourceId;

  const hasCalledNextStep = React.useRef(false);

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  const {
    publicLeadCreationData: { data: { Lead: { _id } = { _id: '' } } = { Lead: { _id: '' } } },
    creditScoreLoading,
  } = useSelector((state: RootState) => state.creditScoreStore);

  // React Hook Form
  const { control, trigger, getValues, setValue, watch } = useFormContext();

  const regionWatcher = watch('address_region');

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    const {
      first,
      last,
      emailAddress,
      dateValue,
      address,
      address_city,
      address_region,
      address_postal,
      address_unit,
      phone,
      address_lookupStreetName,
      address_lookupStreetNumber,
      address_country,
      termsAndConditionConsent,
    } = getValues();

    const updatedData: Lead = {
      firstName: first,
      lastName: last,
      phone: phone,
      email: emailAddress,
      leadSourceId: leadSourceId,
      country: address_country,
      birthDate: dataFormat(dateValue, 'yyyy-MM-dd'),
      zipCode: zipCode || address_postal,
      originalZipCode: address_postal,
      streetName: address_lookupStreetName,
      addressNumber: address_lookupStreetNumber,
      city: address_city,
      leadDetails: {
        City: address_city,
        'Postal Code': address_postal,
        Province: address_region,
        'Street Address': address?.label,
        'Car Year': '1001',
        unit: address_unit,
        addressNumber: address_lookupStreetNumber,
        // associateDealerCode,
        smsNotificationConsent: termsAndConditionConsent,
        'Widget Name': authenticateClientData?.data?.widgetName,
        'Widget ID': authenticateClientData?.data?.widgetId,
        organizationUniqueId: authenticateClientData?.data?.widgetData?.dealershipId,
      },
      province: address_region,
      adfDistributionList: authenticateClientData?.data?.widgetData?.adfDistributionList,
    };

    dispatch(
      postPublicLeadCreationOld({
        data: updatedData,
        leadTypeID: authenticateClientData?.data?.widgetData?.leadTypeId,
      })
    )
      .then(async (res) => {
        Logger.custom('lead_created', {
          id: res?.payload?.data?.Lead?.leadId || '',
          client: authenticateClientData?.data?.client || '',
          widgetName: authenticateClientData?.data?.widgetName || '',
          module: 'Credit Score',
        });
        if ('error' in res && Object.keys(res.error).length && res.error.message) {
          // call another api for lead creation if this api failed
          dispatch(
            postTradeInLeadCreation({
              data: updatedData,
              leadTypeID: authenticateClientData?.data?.widgetData?.leadTypeId,
            })
          );
        } else if (Object.keys(res.payload).length && res.payload.message) {
          onNext?.();
          console.log('publicLeadCreationData', res.payload);
        }
      })
      .catch((error) => {
        hasCalledNextStep.current = false;
        Logger.error('Error in postPublicLeadCreationOld:', {
          errorData: error?.message || error,
          client: authenticateClientData?.data?.client || '',
          widgetName: authenticateClientData?.data?.widgetName || '',
          module: 'Credit Score',
        });
      });
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <div className={`csc-screens__form-col shadow ${creditScoreLoading ? 'position-static' : ''}`}>
      <div className='csc-screens__form-col__top'>
        <div className='csc-screens__form-col__steps'>2/2</div>
        <h3 className='csc-screens__form-col__title'>{t('stepperForm.heading3')}</h3>
      </div>
      <div className='csc-screens__form-col__middle'>
        <div className='csc-screens__field-wrapper'>
          <div className='form-group'>
            <AutoCompleteAddress
              name='address'
              control={control}
              setValue={setValue}
              label={t('stepperForm.address')}
              placeholder={t('stepperForm.enterAddress')}
              rules={formRequiredRules(errorMessage(t).addressRequired)}
              triggerValidationForField={trigger}
            />
          </div>
          <div className='form-group'>
            <CommonInput
              name='address_unit'
              control={control}
              label={t('stepperForm.unitOptional')}
              placeholder={t('stepperForm.enterUnit')}
              triggerValidationForField={trigger}
            />
          </div>
          <div className='form-group'>
            <CommonInput
              name='address_postal'
              control={control}
              label={t('stepperForm.postalCode')}
              placeholder={t('stepperForm.enterPostalCode')}
              rules={formRequiredRules(errorMessage(t).postalRequired)}
              triggerValidationForField={trigger}
            />
          </div>
          <div className='form-group'>
            <CommonInput
              name='address_city'
              control={control}
              label={t('stepperForm.city')}
              placeholder={t('stepperForm.enterCity')}
              rules={formRequiredRules(errorMessage(t).cityRequired)}
              triggerValidationForField={trigger}
            />
          </div>
          <div className='form-group'>
            <SelectInput
              name='address_region'
              control={control}
              label={t('stepperForm.province')}
              rules={formRequiredRules(errorMessage(t).provinceRequired)}
              options={PROVINCE}
              defaultValue={regionWatcher}
              triggerValidationForField={trigger}
            />
          </div>
        </div>
      </div>
      <CustomLoader show={creditScoreLoading} title={t('stepperFormCredit.loaderTxt')} />
    </div>
  );
};

const StepperForm2 = React.forwardRef(Step2);

export default StepperForm2;
